/*
  bn-compare · the controller for every before-and-after wipe on the page.

  Defined once, run twice: inline at parse time so a control already in the DOM works before
  first paint, and again on DOMContentLoaded for anything parsed after. Idempotent, so a
  section that emits this file more than once costs nothing.

  The range input owns the value. The pointer and the keyboard both write to it and everything
  drawn is read back from it, so the two can never disagree about where the wipe is.
*/
(function () {
  if (window.bnCompare) { window.bnCompare(); return; }

  function init(root) {
    if (root.dataset.bnCwInit) return;
    root.dataset.bnCwInit = '1';

    /* The theme editor re-renders a section without reloading the page, so init can run again
       on a fresh root. Everything bound to window goes through this, and is dropped when the
       instance does, rather than leaking a pair of listeners per edit. */
    var ac = window.AbortController ? new AbortController() : null;
    var sig = ac ? { signal: ac.signal } : false;

    var range = root.querySelector('.bn-cw__range');
    if (!range) return;

    /* With no mobile BEFORE plate the control is removed below the breakpoint, so there is
       nothing to drag and nothing should be claiming the gesture. */
    if (root.classList.contains('bn-cw--no-mobile') && window.matchMedia('(max-width: 749px)').matches) return;

    var queued = false;
    function paint() {
      var v = Number(range.value);
      root.style.setProperty('--bn-cmp', v + '%');
      root.style.setProperty('--bn-cmp-n', v / 100);
      range.setAttribute('aria-valuetext', v > 55 ? root.dataset.cwBefore || window.BionoboI18n.s_874c3923fe59314ebd561779 : v < 45 ? root.dataset.cwAfter || window.BionoboI18n.s_cc596f87a4c234fffd143bcc : window.BionoboI18n.s_b182e8227585166ec3842a23);
    }
    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(function () { queued = false; paint(); });
    }

    range.addEventListener('input', schedule);

    /* The whole plate is the handle, not the knob alone. The knob is a thin target and the
       obvious thing to grab is the picture. */
    function setFrom(e) {
      var b = root.getBoundingClientRect();
      if (!b.width) return;
      var pct = ((e.clientX - b.left) / b.width) * 100;
      range.value = pct < 0 ? 0 : pct > 100 ? 100 : pct;
      schedule();
    }

    var dragging = false;
    root.addEventListener('pointerdown', function (e) {
      if (e.button !== undefined && e.button !== 0) return;
      /* Without this the browser starts its own image drag and the gesture is taken away
         mid-stroke, which is what made this feel like it kept letting go. */
      e.preventDefault();
      dragging = true;
      root.classList.add('is-dragging');
      if (root.setPointerCapture) { try { root.setPointerCapture(e.pointerId); } catch (err) {} }
      setFrom(e);
    });
    root.addEventListener('pointermove', function (e) { if (dragging) setFrom(e); });

    function release() {
      if (!dragging) return;
      dragging = false;
      root.classList.remove('is-dragging');
    }
    root.addEventListener('pointerup', release);
    /* pointercancel fires when the browser decides the gesture was a vertical page scroll,
       which touch-action: pan-y allows on purpose. Letting go cleanly there stops the handle
       chasing a finger that has left. */
    root.addEventListener('pointercancel', release);
    root.addEventListener('lostpointercapture', release);

    /* Dragging off the element and releasing outside it still ends the drag, on the browsers
       where pointer capture is unavailable. */
    window.addEventListener('pointerup', release, sig);
    window.addEventListener('blur', release, sig);

    paint();

    /* Arriving already open a little, so the reader can see there are two states without being
       told. Once, and only when the control is actually on screen, so the movement is not
       spent where nobody is looking. Any input from the reader cancels it. */
    var start = Number(root.dataset.cwStart || 0);
    var hintTo = Number(root.dataset.cwHint || 0);
    range.value = start;
    paint();

    if (!hintTo || window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;

    var cancelled = false;
    root.addEventListener('pointerdown', function () { cancelled = true; }, { once: true });
    range.addEventListener('input', function () { cancelled = true; }, { once: true });

    var io = new IntersectionObserver(function (entries) {
      if (!entries[0].isIntersecting) return;
      io.disconnect();
      var t0 = 0;
      root.classList.add('is-animating');
      function step(ts) {
        if (cancelled) { root.classList.remove('is-animating'); return; }
        if (!t0) t0 = ts;
        var p = Math.min(1, (ts - t0) / 900);
        var e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        range.value = start + (hintTo - start) * e;
        paint();
        if (p < 1) window.requestAnimationFrame(step);
        else root.classList.remove('is-animating');
      }
      window.requestAnimationFrame(step);
    }, { threshold: 0.4 });
    io.observe(root);
  }

  window.bnCompare = function () {
    var nodes = document.querySelectorAll('[data-bn-compare]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  };

  document.addEventListener('shopify:section:load', function () { window.bnCompare(); });

  window.bnCompare();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.bnCompare, { once: true });
  }
}());
